import React, { useState, useEffect } from 'react';
import { 
  Save, Plus, Trash2, GripVertical, Eye, EyeOff, 
  ChevronDown, ChevronUp, Type, Image, List, FileText,
  Loader2, ArrowLeft, ExternalLink, Menu, Layout,
  Calendar, HelpCircle, CreditCard, AlignLeft, Mail, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available icons for feature sections
const ICONS = ['Upload', 'Download', 'Shield', 'Zap', 'Clock', 'Users', 'Lock', 'Globe', 'Smartphone', 'CheckCircle', 'Palette', 'Video', 'Megaphone', 'Building', 'FileArchive', 'Mail', 'Star', 'Heart', 'Calendar', 'HelpCircle', 'CreditCard'];
const COLORS = ['blue', 'green', 'yellow', 'purple', 'cyan', 'red', 'orange', 'pink', 'indigo', 'emerald'];

const PageEditor = ({ token, toast, onBack }) => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(res.data.pages || []);
    } catch (error) {
      console.error('Error loading pages:', error);
      toast?.({ title: 'Erreur', description: 'Impossible de charger les pages', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPage = (page) => {
    setSelectedPage({ ...page });
    setEditMode(true);
  };

  const handleSavePage = async () => {
    if (!selectedPage) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${selectedPage.slug}`, selectedPage, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast?.({ title: 'Succès', description: 'Page enregistrée' });
      loadPages();
    } catch (error) {
      toast?.({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur lors de la sauvegarde', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePage = async (slug) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer cette page ? Cette action est irréversible.`)) return;
    
    try {
      await axios.delete(`${API}/admin/pages/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast?.({ title: 'Succès', description: 'Page supprimée' });
      setEditMode(false);
      setSelectedPage(null);
      loadPages();
    } catch (error) {
      toast?.({ title: 'Erreur', description: error.response?.data?.detail || 'Impossible de supprimer cette page', variant: 'destructive' });
    }
  };

  const handleCreatePage = async () => {
    const slug = prompt('Entrez le slug de la nouvelle page (ex: about-us):');
    if (!slug) return;
    
    try {
      await axios.post(`${API}/admin/pages`, {
        slug: slug.toLowerCase().replace(/\s+/g, '-'),
        title: 'Nouvelle page',
        is_published: false,
        sections: []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast?.({ title: 'Succès', description: 'Page créée' });
      loadPages();
    } catch (error) {
      toast?.({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur lors de la création', variant: 'destructive' });
    }
  };

  const updateSection = (sectionIndex, updates) => {
    if (!selectedPage) return;
    const newSections = [...selectedPage.sections];
    newSections[sectionIndex] = { ...newSections[sectionIndex], ...updates };
    setSelectedPage({ ...selectedPage, sections: newSections });
  };

  const addSection = (type) => {
    if (!selectedPage) return;
    const newSection = {
      id: `section_${Date.now()}`,
      type,
      title: 'Nouveau titre',
      description: ''
    };
    
    if (type === 'features_grid') {
      newSection.items = [{ icon: 'Star', title: 'Nouvelle fonctionnalité', description: 'Description', color: 'blue' }];
    } else if (type === 'pricing_plans') {
      newSection.plans = [{ name: 'Plan', price: '0€', period: '', description: 'Description', features: ['Fonctionnalité 1'], cta: 'Action', popular: false }];
    } else if (type === 'use_cases') {
      newSection.items = [{ title: 'Cas d\'usage', description: 'Description', icon: 'Star' }];
    } else if (type === 'timeline') {
      newSection.items = [{ year: '2024', title: 'Événement', description: 'Description de l\'événement', image: '' }];
    } else if (type === 'faq') {
      newSection.items = [{ question: 'Question ?', answer: 'Réponse à la question.' }];
    } else if (type === 'content') {
      newSection.content = '# Titre\n\nVotre contenu ici...';
    } else if (type === 'stats_grid') {
      newSection.items = [{ value: '100+', label: 'Statistique' }];
    } else if (type === 'text_section') {
      newSection.title = 'Titre de la section';
      newSection.description = 'Description de la section texte';
    } else if (type === 'gallery') {
      newSection.title = 'Galerie';
      newSection.subtitle = 'Sous-titre de la galerie';
      newSection.items = [{ image: 'https://via.placeholder.com/600x400', title: 'Image 1', category: 'Catégorie' }];
    } else if (type === 'contact_form') {
      newSection.title = 'Contactez-nous';
      newSection.description = 'Remplissez le formulaire ci-dessous';
    }
    
    setSelectedPage({ ...selectedPage, sections: [...selectedPage.sections, newSection] });
  };

  const removeSection = (index) => {
    if (!selectedPage || !confirm('Supprimer cette section ?')) return;
    const newSections = selectedPage.sections.filter((_, i) => i !== index);
    setSelectedPage({ ...selectedPage, sections: newSections });
  };

  const moveSection = (index, direction) => {
    if (!selectedPage) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedPage.sections.length) return;
    
    const newSections = [...selectedPage.sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setSelectedPage({ ...selectedPage, sections: newSections });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  // Page list view
  if (!editMode) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Gestion des pages</h2>
          <Button onClick={handleCreatePage} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle page
          </Button>
        </div>
        
        <div className="grid gap-4">
          {pages.map((page) => {
            // Determine page URL based on route or slug
            const pageUrl = page.route || (page.page_type === 'hardcoded' ? `/${page.slug}` : `/page/${page.slug}`);
            const isHardcoded = page.page_type === 'hardcoded';
            const isSystem = page.is_system;
            
            return (
            <div 
              key={page.slug}
              className={`bg-slate-800/50 border rounded-xl p-5 transition-all ${isHardcoded ? 'border-slate-500/20 hover:border-slate-500/40' : 'border-blue-500/20 hover:border-blue-500/40'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-lg font-medium text-white">{page.title}</h3>
                    {page.is_published ? (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                        <Eye className="w-3 h-3" /> Publié
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                        <EyeOff className="w-3 h-3" /> Brouillon
                      </span>
                    )}
                    {page.show_in_menu && (
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full flex items-center gap-1">
                        <Menu className="w-3 h-3" /> Menu ({page.menu_order || 10})
                      </span>
                    )}
                    {page.show_in_footer && (
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                        <Layout className="w-3 h-3" /> Footer ({page.footer_order || 10})
                      </span>
                    )}
                    {isHardcoded && (
                      <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Page codée
                      </span>
                    )}
                    {isSystem && !isHardcoded && (
                      <span className="px-2 py-0.5 bg-slate-500/20 text-slate-400 text-xs rounded-full">
                        Système
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-blue-300/60 mt-1">{pageUrl}</p>
                  {isHardcoded ? (
                    <p className="text-sm text-orange-300/40 mt-1">Cette page est codée en React et ne peut pas être éditée via le CMS</p>
                  ) : (
                    <p className="text-sm text-blue-300/40 mt-1">{page.sections?.length || 0} section(s)</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <a 
                    href={pageUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 text-blue-300/60 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors"
                    title="Voir la page"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  {!isHardcoded && (
                    <Button 
                      onClick={() => handleSelectPage(page)}
                      variant="outline"
                      className="border-blue-500/30 text-blue-300"
                    >
                      Modifier
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )})}
        </div>
      </div>
    );
  }

  // Page editor view
  // Check if this is a default page (cannot be deleted)
  const defaultPages = ['features', 'pricing', 'use-cases'];
  const isDefaultPage = defaultPages.includes(selectedPage?.slug);
  const defaultPageSlugs = ['features', 'pricing', 'use-cases', 'resources', 'news'];
  const pageUrl = defaultPageSlugs.includes(selectedPage?.slug) ? `/${selectedPage?.slug}` : `/page/${selectedPage?.slug}`;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => { setEditMode(false); setSelectedPage(null); }}
            className="text-blue-400 hover:text-blue-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-semibold text-white">Modifier: {selectedPage?.title}</h2>
          {isDefaultPage && (
            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
              Page système
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isDefaultPage && (
            <Button 
              onClick={() => handleDeletePage(selectedPage?.slug)}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
            </Button>
          )}
          <a 
            href={pageUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-3 py-2 text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors flex items-center gap-2"
          >
            <Eye className="w-4 h-4" /> Prévisualiser
          </a>
          <Button onClick={handleSavePage} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Page Settings */}
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-5 space-y-4">
        <h3 className="text-white font-medium">Paramètres de la page</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-blue-300/70 mb-1">Titre</label>
            <input 
              type="text"
              value={selectedPage?.title || ''}
              onChange={(e) => setSelectedPage({ ...selectedPage, title: e.target.value })}
              className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm text-blue-300/70 mb-1">Slug (URL)</label>
            <input 
              type="text"
              value={selectedPage?.slug || ''}
              disabled
              className="w-full bg-slate-900/30 border border-blue-500/10 rounded-lg px-3 py-2 text-white/50 cursor-not-allowed"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-blue-300/70 mb-1">Meta description (SEO)</label>
            <textarea 
              value={selectedPage?.meta_description || ''}
              onChange={(e) => setSelectedPage({ ...selectedPage, meta_description: e.target.value })}
              rows={2}
              className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400 resize-none"
            />
          </div>
          
          {/* Publication Status */}
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox"
                checked={selectedPage?.is_published || false}
                onChange={(e) => setSelectedPage({ ...selectedPage, is_published: e.target.checked })}
                className="w-4 h-4 rounded border-blue-500/30 bg-slate-800 text-blue-500 focus:ring-blue-500"
              />
              <span className="text-sm text-blue-300">Publié</span>
            </label>
          </div>
          
          {/* Menu Options */}
          <div className="bg-slate-900/30 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-blue-300/70 text-sm font-medium mb-2">
              <Menu className="w-4 h-4" /> Menu principal
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox"
                checked={selectedPage?.show_in_menu || false}
                onChange={(e) => setSelectedPage({ ...selectedPage, show_in_menu: e.target.checked })}
                className="w-4 h-4 rounded border-blue-500/30 bg-slate-800 text-blue-500 focus:ring-blue-500"
              />
              <span className="text-sm text-blue-300">Afficher dans le menu</span>
            </label>
            {selectedPage?.show_in_menu && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-blue-300/60">Position:</label>
                <input 
                  type="number"
                  min="1"
                  max="99"
                  value={selectedPage?.menu_order || 10}
                  onChange={(e) => setSelectedPage({ ...selectedPage, menu_order: parseInt(e.target.value) || 10 })}
                  className="w-16 bg-slate-800 border border-blue-500/20 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-400"
                />
              </div>
            )}
          </div>
          
          {/* Footer Options */}
          <div className="bg-slate-900/30 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-blue-300/70 text-sm font-medium mb-2">
              <Layout className="w-4 h-4" /> Footer
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox"
                checked={selectedPage?.show_in_footer || false}
                onChange={(e) => setSelectedPage({ ...selectedPage, show_in_footer: e.target.checked })}
                className="w-4 h-4 rounded border-blue-500/30 bg-slate-800 text-blue-500 focus:ring-blue-500"
              />
              <span className="text-sm text-blue-300">Afficher dans le footer</span>
            </label>
            {selectedPage?.show_in_footer && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-blue-300/60">Position:</label>
                <input 
                  type="number"
                  min="1"
                  max="99"
                  value={selectedPage?.footer_order || 10}
                  onChange={(e) => setSelectedPage({ ...selectedPage, footer_order: parseInt(e.target.value) || 10 })}
                  className="w-16 bg-slate-800 border border-blue-500/20 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-400"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-white font-medium">Sections</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => addSection('hero')} variant="outline" size="sm" className="border-blue-500/30 text-blue-300">
              <Type className="w-3 h-3 mr-1" /> Hero
            </Button>
            <Button onClick={() => addSection('features_grid')} variant="outline" size="sm" className="border-blue-500/30 text-blue-300">
              <List className="w-3 h-3 mr-1" /> Grille
            </Button>
            <Button onClick={() => addSection('text')} variant="outline" size="sm" className="border-blue-500/30 text-blue-300">
              <FileText className="w-3 h-3 mr-1" /> Texte
            </Button>
            <Button onClick={() => addSection('text_section')} variant="outline" size="sm" className="border-blue-500/30 text-blue-300">
              <FileText className="w-3 h-3 mr-1" /> Section texte
            </Button>
            <Button onClick={() => addSection('stats_grid')} variant="outline" size="sm" className="border-orange-500/30 text-orange-300">
              <List className="w-3 h-3 mr-1" /> Stats
            </Button>
            <Button onClick={() => addSection('gallery')} variant="outline" size="sm" className="border-pink-500/30 text-pink-300">
              <Image className="w-3 h-3 mr-1" /> Galerie
            </Button>
            <Button onClick={() => addSection('content')} variant="outline" size="sm" className="border-green-500/30 text-green-300">
              <AlignLeft className="w-3 h-3 mr-1" /> Contenu
            </Button>
            <Button onClick={() => addSection('faq')} variant="outline" size="sm" className="border-yellow-500/30 text-yellow-300">
              <HelpCircle className="w-3 h-3 mr-1" /> FAQ
            </Button>
            <Button onClick={() => addSection('timeline')} variant="outline" size="sm" className="border-purple-500/30 text-purple-300">
              <Calendar className="w-3 h-3 mr-1" /> Timeline
            </Button>
            <Button onClick={() => addSection('pricing_plans')} variant="outline" size="sm" className="border-cyan-500/30 text-cyan-300">
              <CreditCard className="w-3 h-3 mr-1" /> Tarifs
            </Button>
            <Button onClick={() => addSection('contact_form')} variant="outline" size="sm" className="border-emerald-500/30 text-emerald-300">
              <Mail className="w-3 h-3 mr-1" /> Formulaire
            </Button>
          </div>
        </div>

        {selectedPage?.sections?.map((section, index) => (
          <SectionEditor 
            key={section.id || index}
            section={section}
            index={index}
            total={selectedPage.sections.length}
            onUpdate={(updates) => updateSection(index, updates)}
            onRemove={() => removeSection(index)}
            onMove={(dir) => moveSection(index, dir)}
          />
        ))}

        {(!selectedPage?.sections || selectedPage.sections.length === 0) && (
          <div className="text-center py-12 bg-slate-800/30 border border-dashed border-blue-500/20 rounded-xl">
            <FileText className="w-10 h-10 text-blue-300/30 mx-auto mb-3" />
            <p className="text-blue-300/50">Aucune section. Ajoutez-en une ci-dessus.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Section Editor Component
const SectionEditor = ({ section, index, total, onUpdate, onRemove, onMove }) => {
  const [expanded, setExpanded] = useState(true);

  const getSectionLabel = (type) => {
    const labels = {
      'hero': 'Section Hero',
      'features_grid': 'Grille de fonctionnalités',
      'pricing_plans': 'Plans tarifaires',
      'use_cases': 'Cas d\'usage',
      'text': 'Texte libre',
      'text_section': 'Section texte',
      'content': 'Contenu riche',
      'faq': 'Questions fréquentes',
      'timeline': 'Chronologie',
      'stats_grid': 'Grille de statistiques',
      'gallery': 'Galerie d\'images',
      'contact_form': 'Formulaire de contact'
    };
    return labels[type] || type;
  };

  return (
    <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl overflow-hidden">
      {/* Section Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-800/70"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <GripVertical className="w-4 h-4 text-blue-300/40" />
          <span className="text-white font-medium">{getSectionLabel(section.type)}</span>
          <span className="text-xs text-blue-300/50">#{index + 1}</span>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={(e) => { e.stopPropagation(); onMove('up'); }}
            disabled={index === 0}
            className="p-1 text-blue-300/60 hover:text-blue-300 disabled:opacity-30"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onMove('down'); }}
            disabled={index === total - 1}
            className="p-1 text-blue-300/60 hover:text-blue-300 disabled:opacity-30"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-1 text-red-400/60 hover:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <ChevronDown className={`w-4 h-4 text-blue-300/60 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Section Content */}
      {expanded && (
        <div className="p-4 pt-0 space-y-4 border-t border-blue-500/10">
          {/* Hero Section */}
          {section.type === 'hero' && (
            <>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Titre principal</label>
                <input 
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Sous-titre</label>
                <input 
                  type="text"
                  value={section.subtitle || ''}
                  onChange={(e) => onUpdate({ subtitle: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Description</label>
                <textarea 
                  value={section.description || ''}
                  onChange={(e) => onUpdate({ description: e.target.value })}
                  rows={3}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400 resize-none"
                />
              </div>
            </>
          )}

          {/* Features Grid */}
          {section.type === 'features_grid' && (
            <FeatureGridEditor 
              items={section.items || []}
              onUpdate={(items) => onUpdate({ items })}
            />
          )}

          {/* Text Section */}
          {section.type === 'text' && (
            <div>
              <label className="block text-sm text-blue-300/70 mb-1">Contenu (Markdown supporté)</label>
              <textarea 
                value={section.content || ''}
                onChange={(e) => onUpdate({ content: e.target.value })}
                rows={8}
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400 resize-none font-mono text-sm"
                placeholder="# Titre&#10;&#10;Votre contenu ici..."
              />
            </div>
          )}

          {/* Use Cases */}
          {section.type === 'use_cases' && (
            <UseCasesEditor 
              items={section.items || []}
              onUpdate={(items) => onUpdate({ items })}
            />
          )}

          {/* Content Section (rich text for About, Legal pages) */}
          {section.type === 'content' && (
            <>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Titre de section (optionnel)</label>
                <input 
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                  placeholder="Laisser vide si pas de titre"
                />
              </div>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Contenu (Markdown supporté: # ## ### ** - pour listes)</label>
                <textarea 
                  value={section.content || ''}
                  onChange={(e) => onUpdate({ content: e.target.value })}
                  rows={12}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400 resize-y font-mono text-sm"
                  placeholder="# Titre&#10;&#10;**Texte en gras**&#10;&#10;- Liste à puces&#10;&#10;Paragraphe normal..."
                />
              </div>
            </>
          )}

          {/* FAQ Section */}
          {section.type === 'faq' && (
            <>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Titre de la section FAQ</label>
                <input 
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                  placeholder="Ex: Questions fréquentes"
                />
              </div>
              <FAQEditor 
                items={section.items || []}
                onUpdate={(items) => onUpdate({ items })}
              />
            </>
          )}

          {/* Timeline Section */}
          {section.type === 'timeline' && (
            <>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Titre de la chronologie</label>
                <input 
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                  placeholder="Ex: Notre histoire"
                />
              </div>
              <TimelineEditor 
                items={section.items || []}
                onUpdate={(items) => onUpdate({ items })}
              />
            </>
          )}

          {/* Stats Grid Section */}
          {section.type === 'stats_grid' && (
            <>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Titre (optionnel)</label>
                <input 
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                  placeholder="Ex: Nos chiffres clés"
                />
              </div>
              <StatsGridEditor 
                items={section.items || []}
                onUpdate={(items) => onUpdate({ items })}
              />
            </>
          )}

          {/* Text Section (title + description) */}
          {section.type === 'text_section' && (
            <>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Titre</label>
                <input 
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Description</label>
                <textarea 
                  value={section.description || ''}
                  onChange={(e) => onUpdate({ description: e.target.value })}
                  rows={4}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400 resize-none"
                />
              </div>
            </>
          )}

          {/* Gallery Section */}
          {section.type === 'gallery' && (
            <>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Titre</label>
                <input 
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Sous-titre</label>
                <input 
                  type="text"
                  value={section.subtitle || ''}
                  onChange={(e) => onUpdate({ subtitle: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                />
              </div>
              <GalleryEditor 
                items={section.items || []}
                onUpdate={(items) => onUpdate({ items })}
              />
            </>
          )}

          {/* Contact Form Section */}
          {section.type === 'contact_form' && (
            <>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Titre du formulaire</label>
                <input 
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                  placeholder="Ex: Contactez-nous"
                />
              </div>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Description</label>
                <textarea 
                  value={section.description || ''}
                  onChange={(e) => onUpdate({ description: e.target.value })}
                  rows={3}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400 resize-none"
                  placeholder="Instructions pour remplir le formulaire..."
                />
              </div>
              <p className="text-xs text-blue-300/50 mt-2">
                Le formulaire est généré automatiquement. Seuls le titre et la description sont personnalisables.
              </p>
            </>
          )}

          {/* Pricing Plans Section */}
          {section.type === 'pricing_plans' && (
            <>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1">Titre</label>
                <input 
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => onUpdate({ title: e.target.value })}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                  placeholder="Ex: Nos tarifs"
                />
              </div>
              <PricingPlansEditor 
                plans={section.plans || []}
                onUpdate={(plans) => onUpdate({ plans })}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Feature Grid Editor
const FeatureGridEditor = ({ items, onUpdate }) => {
  const addItem = () => {
    onUpdate([...items, { icon: 'Star', title: 'Nouvelle fonctionnalité', description: 'Description', color: 'blue' }]);
  };

  const updateItem = (index, updates) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onUpdate(newItems);
  };

  const removeItem = (index) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="bg-slate-900/30 border border-blue-500/10 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-blue-300/50">Élément #{index + 1}</span>
            <button onClick={() => removeItem(index)} className="text-red-400/60 hover:text-red-400">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select 
              value={item.icon || 'Star'}
              onChange={(e) => updateItem(index, { icon: e.target.value })}
              className="bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
            >
              {ICONS.map(icon => <option key={icon} value={icon}>{icon}</option>)}
            </select>
            <select 
              value={item.color || 'blue'}
              onChange={(e) => updateItem(index, { color: e.target.value })}
              className="bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
            >
              {COLORS.map(color => <option key={color} value={color}>{color}</option>)}
            </select>
          </div>
          <input 
            type="text"
            value={item.title || ''}
            onChange={(e) => updateItem(index, { title: e.target.value })}
            placeholder="Titre"
            className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
          />
          <textarea 
            value={item.description || ''}
            onChange={(e) => updateItem(index, { description: e.target.value })}
            placeholder="Description"
            rows={2}
            className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white resize-none"
          />
        </div>
      ))}
      <button 
        onClick={addItem}
        className="w-full py-2 border border-dashed border-blue-500/30 rounded-lg text-blue-300/60 hover:text-blue-300 hover:border-blue-500/50 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Ajouter un élément
      </button>
    </div>
  );
};

// Use Cases Editor
const UseCasesEditor = ({ items, onUpdate }) => {
  const addItem = () => {
    onUpdate([...items, { icon: 'Star', title: 'Nouveau cas d\'usage', description: 'Description' }]);
  };

  const updateItem = (index, updates) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onUpdate(newItems);
  };

  const removeItem = (index) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="bg-slate-900/30 border border-blue-500/10 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-blue-300/50">Cas d'usage #{index + 1}</span>
            <button onClick={() => removeItem(index)} className="text-red-400/60 hover:text-red-400">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <select 
            value={item.icon || 'Star'}
            onChange={(e) => updateItem(index, { icon: e.target.value })}
            className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
          >
            {ICONS.map(icon => <option key={icon} value={icon}>{icon}</option>)}
          </select>
          <input 
            type="text"
            value={item.title || ''}
            onChange={(e) => updateItem(index, { title: e.target.value })}
            placeholder="Titre"
            className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
          />
          <textarea 
            value={item.description || ''}
            onChange={(e) => updateItem(index, { description: e.target.value })}
            placeholder="Description"
            rows={2}
            className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white resize-none"
          />
        </div>
      ))}
      <button 
        onClick={addItem}
        className="w-full py-2 border border-dashed border-blue-500/30 rounded-lg text-blue-300/60 hover:text-blue-300 hover:border-blue-500/50 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Ajouter un cas d'usage
      </button>
    </div>
  );
};

// FAQ Editor
const FAQEditor = ({ items, onUpdate }) => {
  const addItem = () => {
    onUpdate([...items, { question: 'Nouvelle question ?', answer: 'Réponse à la question.' }]);
  };

  const updateItem = (index, updates) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onUpdate(newItems);
  };

  const removeItem = (index) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  const moveItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    onUpdate(newItems);
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="bg-slate-900/30 border border-yellow-500/10 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-yellow-300/50">Question #{index + 1}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="p-1 text-blue-300/60 hover:text-blue-300 disabled:opacity-30">
                <ChevronUp className="w-3 h-3" />
              </button>
              <button onClick={() => moveItem(index, 'down')} disabled={index === items.length - 1} className="p-1 text-blue-300/60 hover:text-blue-300 disabled:opacity-30">
                <ChevronDown className="w-3 h-3" />
              </button>
              <button onClick={() => removeItem(index)} className="p-1 text-red-400/60 hover:text-red-400">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
          <input 
            type="text"
            value={item.question || ''}
            onChange={(e) => updateItem(index, { question: e.target.value })}
            placeholder="Question"
            className="w-full bg-slate-800 border border-yellow-500/20 rounded px-2 py-1.5 text-sm text-white font-medium"
          />
          <textarea 
            value={item.answer || ''}
            onChange={(e) => updateItem(index, { answer: e.target.value })}
            placeholder="Réponse"
            rows={3}
            className="w-full bg-slate-800 border border-yellow-500/20 rounded px-2 py-1.5 text-sm text-white resize-none"
          />
        </div>
      ))}
      <button 
        onClick={addItem}
        className="w-full py-2 border border-dashed border-yellow-500/30 rounded-lg text-yellow-300/60 hover:text-yellow-300 hover:border-yellow-500/50 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Ajouter une question
      </button>
    </div>
  );
};

// Timeline Editor
const TimelineEditor = ({ items, onUpdate }) => {
  const [uploading, setUploading] = useState({});

  const addItem = () => {
    onUpdate([...items, { year: new Date().getFullYear().toString(), title: 'Nouvel événement', description: 'Description de l\'événement', image: '' }]);
  };

  const updateItem = (index, updates) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onUpdate(newItems);
  };

  const removeItem = (index) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  const moveItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    onUpdate(newItems);
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [index]: true }));
    try {
      const token = localStorage.getItem('sendbox_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/cms/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.image_url) {
        updateItem(index, { image: res.data.image_url });
      }
    } catch (e) {
      console.error('Upload error:', e);
    }
    setUploading(prev => ({ ...prev, [index]: false }));
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="bg-slate-900/30 border border-purple-500/10 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-purple-300/50">Événement #{index + 1}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="p-1 text-blue-300/60 hover:text-blue-300 disabled:opacity-30">
                <ChevronUp className="w-3 h-3" />
              </button>
              <button onClick={() => moveItem(index, 'down')} disabled={index === items.length - 1} className="p-1 text-blue-300/60 hover:text-blue-300 disabled:opacity-30">
                <ChevronDown className="w-3 h-3" />
              </button>
              <button onClick={() => removeItem(index)} className="p-1 text-red-400/60 hover:text-red-400">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="block text-xs text-purple-300/50 mb-1">Année</label>
              <input 
                type="text"
                value={item.year || ''}
                onChange={(e) => updateItem(index, { year: e.target.value })}
                placeholder="2024"
                className="w-full bg-slate-800 border border-purple-500/20 rounded px-2 py-1.5 text-sm text-white font-bold"
              />
            </div>
            <div className="col-span-3">
              <label className="block text-xs text-purple-300/50 mb-1">Titre</label>
              <input 
                type="text"
                value={item.title || ''}
                onChange={(e) => updateItem(index, { title: e.target.value })}
                placeholder="Titre de l'événement"
                className="w-full bg-slate-800 border border-purple-500/20 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-purple-300/50 mb-1">Description</label>
            <textarea 
              value={item.description || ''}
              onChange={(e) => updateItem(index, { description: e.target.value })}
              placeholder="Description de l'événement"
              rows={2}
              className="w-full bg-slate-800 border border-purple-500/20 rounded px-2 py-1.5 text-sm text-white resize-none"
            />
          </div>
          <div>
            <label className="block text-xs text-purple-300/50 mb-1">Image (optionnel)</label>
            <div className="flex gap-2 items-center">
              <input 
                type="text"
                value={item.image || ''}
                onChange={(e) => updateItem(index, { image: e.target.value })}
                placeholder="/images/screenshot.png ou URL"
                className="flex-1 bg-slate-800 border border-purple-500/20 rounded px-2 py-1.5 text-sm text-white"
                data-testid={`timeline-image-url-${index}`}
              />
              <label className={`cursor-pointer px-3 py-1.5 rounded text-xs font-medium transition-all flex items-center gap-1.5 ${
                uploading[index] 
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                  : 'bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30'
              }`} data-testid={`timeline-upload-btn-${index}`}>
                {uploading[index] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Image className="w-3.5 h-3.5" />}
                {uploading[index] ? 'Upload...' : 'Uploader'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(index, e.target.files[0])}
                  disabled={uploading[index]}
                />
              </label>
              {item.image && (
                <button
                  onClick={() => updateItem(index, { image: '' })}
                  className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                  title="Supprimer l'image"
                  data-testid={`timeline-remove-image-${index}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          {item.image && (
            <div className="mt-2 relative group">
              <img 
                src={item.image.startsWith('/') ? `${BACKEND_URL}${item.image}` : item.image} 
                alt="Preview" 
                className="h-24 w-auto rounded border border-purple-500/20 object-cover" 
                onError={(e) => e.target.style.display='none'} 
              />
            </div>
          )}
        </div>
      ))}
      <button 
        onClick={addItem}
        className="w-full py-2 border border-dashed border-purple-500/30 rounded-lg text-purple-300/60 hover:text-purple-300 hover:border-purple-500/50 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Ajouter un événement
      </button>
    </div>
  );
};

// Pricing Plans Editor
const PricingPlansEditor = ({ plans, onUpdate }) => {
  const addPlan = () => {
    onUpdate([...plans, { 
      name: 'Nouveau plan', 
      price: '0€', 
      period: '/mois',
      description: 'Description du plan', 
      features: ['Fonctionnalité 1', 'Fonctionnalité 2'], 
      cta: 'Choisir',
      cta_link: '/pricing',
      popular: false 
    }]);
  };

  const updatePlan = (index, updates) => {
    const newPlans = [...plans];
    newPlans[index] = { ...newPlans[index], ...updates };
    onUpdate(newPlans);
  };

  const removePlan = (index) => {
    onUpdate(plans.filter((_, i) => i !== index));
  };

  const addFeature = (planIndex) => {
    const newPlans = [...plans];
    newPlans[planIndex].features = [...(newPlans[planIndex].features || []), 'Nouvelle fonctionnalité'];
    onUpdate(newPlans);
  };

  const updateFeature = (planIndex, featureIndex, value) => {
    const newPlans = [...plans];
    newPlans[planIndex].features[featureIndex] = value;
    onUpdate(newPlans);
  };

  const removeFeature = (planIndex, featureIndex) => {
    const newPlans = [...plans];
    newPlans[planIndex].features = newPlans[planIndex].features.filter((_, i) => i !== featureIndex);
    onUpdate(newPlans);
  };

  return (
    <div className="space-y-4">
      {plans.map((plan, planIndex) => (
        <div key={planIndex} className={`border rounded-xl p-4 space-y-3 ${plan.popular ? 'bg-cyan-500/5 border-cyan-500/30' : 'bg-slate-900/30 border-blue-500/10'}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white">Plan #{planIndex + 1}</span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox"
                  checked={plan.popular || false}
                  onChange={(e) => updatePlan(planIndex, { popular: e.target.checked })}
                  className="w-4 h-4 rounded border-cyan-500/30 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                />
                <span className="text-xs text-cyan-300">Populaire</span>
              </label>
              <button onClick={() => removePlan(planIndex)} className="p-1 text-red-400/60 hover:text-red-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-blue-300/50 mb-1">Nom du plan</label>
              <input 
                type="text"
                value={plan.name || ''}
                onChange={(e) => updatePlan(planIndex, { name: e.target.value })}
                placeholder="Pro"
                className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-blue-300/50 mb-1">Prix</label>
                <input 
                  type="text"
                  value={plan.price || ''}
                  onChange={(e) => updatePlan(planIndex, { price: e.target.value })}
                  placeholder="9.99€"
                  className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-blue-300/50 mb-1">Période</label>
                <input 
                  type="text"
                  value={plan.period || ''}
                  onChange={(e) => updatePlan(planIndex, { period: e.target.value })}
                  placeholder="/mois"
                  className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
                />
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-xs text-blue-300/50 mb-1">Description</label>
            <input 
              type="text"
              value={plan.description || ''}
              onChange={(e) => updatePlan(planIndex, { description: e.target.value })}
              placeholder="Pour les professionnels"
              className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
            />
          </div>
          
          <div>
            <label className="block text-xs text-blue-300/50 mb-1">Fonctionnalités</label>
            <div className="space-y-1">
              {(plan.features || []).map((feature, featureIndex) => (
                <div key={featureIndex} className="flex items-center gap-2">
                  <input 
                    type="text"
                    value={feature}
                    onChange={(e) => updateFeature(planIndex, featureIndex, e.target.value)}
                    className="flex-1 bg-slate-800 border border-blue-500/20 rounded px-2 py-1 text-sm text-white"
                  />
                  <button onClick={() => removeFeature(planIndex, featureIndex)} className="p-1 text-red-400/60 hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button 
                onClick={() => addFeature(planIndex)}
                className="w-full py-1 border border-dashed border-blue-500/20 rounded text-blue-300/50 hover:text-blue-300 text-xs flex items-center justify-center gap-1"
              >
                <Plus className="w-3 h-3" /> Ajouter une fonctionnalité
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-blue-300/50 mb-1">Texte du bouton</label>
              <input 
                type="text"
                value={plan.cta || ''}
                onChange={(e) => updatePlan(planIndex, { cta: e.target.value })}
                placeholder="Choisir"
                className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-blue-300/50 mb-1">Lien du bouton</label>
              <input 
                type="text"
                value={plan.cta_link || ''}
                onChange={(e) => updatePlan(planIndex, { cta_link: e.target.value })}
                placeholder="/pricing"
                className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
          </div>
        </div>
      ))}
      <button 
        onClick={addPlan}
        className="w-full py-2 border border-dashed border-cyan-500/30 rounded-lg text-cyan-300/60 hover:text-cyan-300 hover:border-cyan-500/50 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Ajouter un plan tarifaire
      </button>
    </div>
  );
};

// Stats Grid Editor
const StatsGridEditor = ({ items, onUpdate }) => {
  const addItem = () => {
    onUpdate([...items, { value: '100+', label: 'Nouvelle statistique' }]);
  };

  const updateItem = (index, updates) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onUpdate(newItems);
  };

  const removeItem = (index) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="bg-slate-900/30 border border-orange-500/10 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-orange-300/50">Statistique #{index + 1}</span>
            <button onClick={() => removeItem(index)} className="text-red-400/60 hover:text-red-400">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-blue-300/50 mb-1">Valeur</label>
              <input 
                type="text"
                value={item.value || ''}
                onChange={(e) => updateItem(index, { value: e.target.value })}
                placeholder="Ex: 2M+"
                className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-blue-300/50 mb-1">Label</label>
              <input 
                type="text"
                value={item.label || ''}
                onChange={(e) => updateItem(index, { label: e.target.value })}
                placeholder="Ex: Utilisateurs"
                className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
          </div>
        </div>
      ))}
      <button 
        onClick={addItem}
        className="w-full py-2 border border-dashed border-orange-500/30 rounded-lg text-orange-300/60 hover:text-orange-300 hover:border-orange-500/50 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Ajouter une statistique
      </button>
    </div>
  );
};

// Gallery Editor
const GalleryEditor = ({ items, onUpdate }) => {
  const addItem = () => {
    onUpdate([...items, { image: '', title: 'Nouvelle image', category: 'Catégorie' }]);
  };

  const updateItem = (index, updates) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onUpdate(newItems);
  };

  const removeItem = (index) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="bg-slate-900/30 border border-pink-500/10 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-pink-300/50">Image #{index + 1}</span>
            <button onClick={() => removeItem(index)} className="text-red-400/60 hover:text-red-400">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <div>
            <label className="block text-xs text-blue-300/50 mb-1">URL de l'image</label>
            <input 
              type="text"
              value={item.image || ''}
              onChange={(e) => updateItem(index, { image: e.target.value })}
              placeholder="https://example.com/image.jpg"
              className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-blue-300/50 mb-1">Titre</label>
              <input 
                type="text"
                value={item.title || ''}
                onChange={(e) => updateItem(index, { title: e.target.value })}
                placeholder="Titre de l'image"
                className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-blue-300/50 mb-1">Catégorie</label>
              <input 
                type="text"
                value={item.category || ''}
                onChange={(e) => updateItem(index, { category: e.target.value })}
                placeholder="Ex: Événementiel"
                className="w-full bg-slate-800 border border-blue-500/20 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
          </div>
          {item.image && (
            <div className="mt-2">
              <img src={item.image} alt={item.title} className="w-full h-24 object-cover rounded" />
            </div>
          )}
        </div>
      ))}
      <button 
        onClick={addItem}
        className="w-full py-2 border border-dashed border-pink-500/30 rounded-lg text-pink-300/60 hover:text-pink-300 hover:border-pink-500/50 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Ajouter une image
      </button>
    </div>
  );
};

export default PageEditor;
